import { Z3kRoutes } from 'z3k-settings/routes/type';
import { isMediumUI } from 'shared/app/lib/choose-ui-type';
import { useNavigate } from 'react-router';
import { usePartial } from 'shared/hooks/utils';
import { getBoilerModels } from 'console/redux/boiler-models/action';
import { useAction, useAppSelector, usePartialAppSelector, } from 'shared/hooks/redux/hooks';
import { selectDevice } from 'entities/device/lib';
import { UpdateRouterDeviceId } from 'z3k-settings/redux/reducer';
import { LegacyDevice } from 'console/device/device';
import { Capability } from 'console/device/device-capability';
import { openDeviceSettingsDialog } from 'console/redux/main-actions';
import { useCallback, useMemo } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { createSubsetSelector } from 'utils';
import { DevicePermission } from 'console/device/device_ts';
import { requestDeviceSpec } from 'console/redux/device_spec/actions';
const useOpenZ3kRouterDialog = (device_id) => {
    const navigate = useNavigate();
    const updateDeviceId = usePartial(useAction(UpdateRouterDeviceId), device_id);
    return (path, secondPath, objectId, is_climatic) => {
        updateDeviceId();
        if (objectId) {
            navigate(`settings/${path}/${secondPath}`, {
                state: {
                    group_item: path,
                    item_key: secondPath,
                    item_id: objectId,
                    is_climatic: !!is_climatic,
                },
            });
        }
        else if (path && isMediumUI()) {
            navigate(`settings/${path}`, {
                state: {
                    group_item: path,
                    item_key: null,
                    item_id: null,
                    is_climatic: !!is_climatic,
                },
            });
        }
        else {
            navigate(`settings`, {
                state: {
                    group_item: null,
                    item_key: null,
                    item_id: null,
                    is_climatic: !!is_climatic,
                },
            });
        }
    };
};
const deviceSelector = createSelector(selectDevice, createSubsetSelector(['id', 'capabilities', 'access', 'user_id']));
export const useOpenDeviceSettings = (device_id) => {
    const device = usePartialAppSelector(deviceSelector, device_id);
    const loadBoilersModels = useAction(getBoilerModels);
    const loadDeviceSpec = useAction(requestDeviceSpec);
    const boilers_loaded = useAppSelector((state) => state.boiler_models.loaded);
    const is_climatic = useMemo(() => device?.capabilities != null
        ? LegacyDevice.has_capability(device.capabilities, Capability.has_climatic_interface)
        : false, [device?.capabilities]);
    const is_z3k_device = useMemo(() => device?.capabilities != null
        ? LegacyDevice.has_capability(device.capabilities, Capability.has_z3k_settings)
        : false, [device?.capabilities]);
    const is_thermostat = useMemo(() => device?.capabilities != null
        ? LegacyDevice.has_capability(device.capabilities, Capability.has_thermostat) ||
            LegacyDevice.has_capability(device.capabilities, Capability.has_optional_thermostat)
        : false, [device?.capabilities]);
    const openZ3kDialog = useOpenZ3kRouterDialog(device_id);
    const onOpenDeviceSettingsDialog = useAction(openDeviceSettingsDialog);
    const account_id = useAppSelector((state) => state.account.id);
    const show_settings_button = useMemo(() => !!(device?.user_id == account_id ||
        device?.access?.some(({ user_id, perms }) => user_id === account_id &&
            perms.includes(DevicePermission.show_configure))), [account_id, device?.access, device?.user_id]);
    const resultFunction = useCallback((e, groupKey, itemKey, objectId) => {
        if (!boilers_loaded && is_thermostat) {
            loadBoilersModels();
        }
        if (is_climatic || is_z3k_device) {
            loadDeviceSpec();
        }
        if (is_climatic && e.altKey) {
            onOpenDeviceSettingsDialog(false, device_id);
        }
        else if (is_climatic && e.shiftKey) {
            openZ3kDialog(groupKey ?? Z3kRoutes.settings, itemKey, objectId, false);
        }
        else if (is_z3k_device) {
            openZ3kDialog(groupKey ?? Z3kRoutes.settings, itemKey, objectId, is_climatic);
        }
        else {
            onOpenDeviceSettingsDialog(e.ctrlKey, device_id);
        }
    }, [
        device_id,
        boilers_loaded,
        is_thermostat,
        loadBoilersModels,
        is_climatic,
        openZ3kDialog,
        onOpenDeviceSettingsDialog,
    ]);
    return [show_settings_button, resultFunction];
};
